import { authAxios, AUTH_API_BASE } from '../app/authAxios';

/**
 * Shared download helper for template/document files.
 *
 * Two genuinely different situations get two different mechanisms:
 *
 * 1. Same-origin — a protected route on our own API. A plain `<a>` click
 *    is a fresh, unauthenticated navigation that never carries the Bearer
 *    token, so this goes through authAxios as a blob fetch (carrying the
 *    auth header) and gets handed to the browser as an in-memory blob URL.
 *
 * 2. Cross-origin — confirmed live: KYC template `download_url`s are
 *    pre-signed Backblaze B2 URLs
 *    (`https://cavan-ims.s3.us-east-005.backblazeb2.com/...?X-Amz-Signature=...`),
 *    already fully authenticated by that query-string signature, no Bearer
 *    token needed or wanted. This does NOT go through a JS-side blob
 *    fetch: the bucket's own CORS policy governs whether a script-
 *    initiated read is even allowed, and testing showed it isn't — the
 *    network tab shows a clean 200 (the transfer itself completes fine),
 *    but the browser still blocks the JS response read because CORS is a
 *    same-origin-policy relaxation for *scripts*, not for the transport
 *    itself, and B2 doesn't return the necessary Access-Control-Allow-
 *    Origin header for this app's origin. That's out of this app's
 *    control (it's the bucket's CORS config, not ours), so the fix is to
 *    stop trying to read the bytes in JS at all: a plain top-level
 *    navigation via a `download`-attributed anchor isn't subject to CORS
 *    the way a script-initiated fetch/XHR is, and is what actually works
 *    for a fully public, pre-signed URL like this one.
 */
function isSameOriginAsApi(url) {
  try {
    return new URL(url, AUTH_API_BASE).origin === new URL(AUTH_API_BASE).origin;
  } catch {
    // Relative/unparseable — treat as our own API rather than risk
    // silently dropping the auth header a real protected route needs.
    return true;
  }
}

// "attachment; filename=\"fixed-income-agreement.pdf\"" — quotes are
// optional per the header spec, so the regex tolerates either.
function extractFilename(contentDisposition, fallback) {
  if (!contentDisposition) return fallback;
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(contentDisposition);
  return match ? decodeURIComponent(match[1]) : fallback;
}

export function triggerBrowserDownload(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

// Plain top-level navigation, not a script-side fetch — this is what
// actually works for a pre-signed, publicly-readable storage URL (see file
// header). `download` asks the browser to save rather than display the
// file; browsers that honor it cross-origin will save under this name,
// browsers that don't will open it in a new tab instead (still lets the
// person save it themselves via the browser's own UI) — either way this
// can't fail the way a blocked CORS read does.
function navigateToDownload(url, filename) {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.target = '_blank';
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

/**
 * Downloads `url` to the person's system. Same-origin protected routes go
 * through an authenticated blob fetch (see file header); anything else
 * (pre-signed storage URLs) goes through a direct browser navigation
 * instead of a JS-side fetch, since that's the only mechanism CORS doesn't
 * stand in the way of. Returns `{ ok: true }` or `{ ok: false, error }`
 * rather than throwing, so a calling component can show a toast without
 * its own try/catch — the cross-origin path can't detect failure the way
 * a fetch can, so it always reports success once the navigation is fired.
 */
export async function downloadFileFromUrl(url, fallbackFilename = 'download') {
  if (!isSameOriginAsApi(url)) {
    navigateToDownload(url, fallbackFilename);
    return { ok: true, filename: fallbackFilename };
  }

  try {
    const response = await authAxios({ url, method: 'GET', responseType: 'blob' });
    const filename = extractFilename(response.headers?.['content-disposition'], fallbackFilename);
    triggerBrowserDownload(response.data, filename);
    return { ok: true, filename };
  } catch (err) {
    return { ok: false, error: err };
  }
}

export default downloadFileFromUrl;
